import { useTranslation } from 'react-i18next'
import {
  Box,
  Checkbox,
  Chip,
  createTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { COLORS } from "colors/color";
import React, { useEffect, useState, useRef } from "react";
import { FieldTypography } from "StyledTypography/StyledTypography";
import CloseIcon from "@mui/icons-material/Close";
import { CancelButton } from "components/Buttons/Buttons";
import { NextButton } from "components/Buttons/Buttons";
import ImageUploadButton from "views/ImageUploadButton";
import SalaryInputWithCurrency from "components/Inputs/SalaryInputWithCurrency";
import GlobalCityAutocomplete from "components/CitiesPicker/CitiesPicker";
import TextEditor from "components/TextFields/EditorTextField";
import { NewCancelButton } from "components/Buttons/Buttons";
import { NewCreateOfferButton } from "components/Buttons/Buttons";
import MatchingCoefficients from "./MatchingCoefficients";

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "15px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
  },
});

const StyledDialogContent = styled(DialogContent)({
  paddingX: 19,
});
const StyledFormControl = styled(FormControl)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#0017E7',
      borderWidth: '1px',
    },
    '&:hover': {
      transition: '0.3s ease',
      boxShadow: 'none',
    },
    '&.Mui-focused': {
      boxShadow: 'none',
      borderColor: '#0017E7',
      borderWidth: '2px',
      "& fieldset": {
        borderColor: "transparent",
        borderWidth: "2px",
      },
    },
    '&.Mui-error': {
      '&:hover, &.Mui-focused': {
        boxShadow: 'none',
        borderColor: '#d32f2f',
      },
    },
  },
});
const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: "16px",
  "&.MuiChip-outlined": {
    borderColor: "#00000033",
  },
}));
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    marginLeft: "20px",
    width: "250px",
    borderRadius: "10px",
    fontFamily: "Poppins",
    borderColor: "#00000033",
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e0e0e0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      transition: "0.3s ease",
      borderColor: "transparent",
      borderWidth: "2px",
      boxShadow: "0px 0px 5px 0.4px #0085FF",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
      borderWidth: "2px",
      boxShadow: "0px 0px 5px 0.4px #0085FF",
    },
    "& .MuiOutlinedInput-input": {
      minHeight: "36px",
      padding: "8px 14px",
    },
  },
}));
function EditWorkingDetailsDialog({
  setOpen2 = () => { },
  setOpen = () => { },
  open2 = false,
  errors = {},
  handleImageUpload = () => { },
  setWorkingMode = () => { },
  workingMode = "Remote",
  location = "",
  setLocation = () => { },
  setErrors = () => { },
  numPeople = 1,
  setNumPeople = () => { },
  salary = "",
  handleSalaryChange = () => { },
  currency = "MAD",
  setCurrency = () => { },
  isChecked = false,
  handleCheckboxChange = () => { },
  isCheckedScr = false,
  handleCheckboxChangeScr = () => { },
  handleSave = () => { },
  experience = "",
  setExperience = () => { },
  diploma = "",
  setDiploma = () => { },
  otherDiploma = "",
  setOtherDiploma = () => { },
  fieldOfStudy = "",
  setFieldOfStudy = () => { },
  otherFieldOfStudy = "",
  setOtherFieldOfStudy = () => { },
  job = {},
  postTitle = "",
  postDescription = "",
  offerType = "",
  postRequirement = "",
  postDeadline = "",
  handleDateChange = () => { },
  accountType = "",
  reference = "",
  recruiterEmail = "",
  onUpdateSuccess = () => { },
  onNormalizedCoefficientsChange
}) {

  const [showOtherFieldInput, setShowOtherFieldInput] = useState(false);
  const [showOtherDiplomaInput, setShowOtherDiplomaInput] = useState(false);
  const initializedRef = useRef(false);
  const { t } = useTranslation();

  // Coefficients state
  const [coefficientsDropdownOpen, setCoefficientsDropdownOpen] = useState(false);
  const [coefficients, setCoefficients] = useState({
    profile_match: 0.20,
    skills_match: 0.30,
    experience_match: 0.35,
    education_match: 0.15
  });
  const [normaliseCoefficients, setNormaliseCoefficients] = useState({
    "profile_match": "0.200",
    "skills_match": "0.300",
    "experience_match": "0.210",
    "experience_years_match": "0.140",
    "education_match": "0.150"
  });
  const previousCoefficientsRef = useRef();
  const dropdownRef = useRef(null);
  const round2 = (num) => num.toFixed(3);

  useEffect(() => {
    if (coefficients) {
      // Calculate the total of the main coefficients
      const coefficientsTotal = coefficients.profile_match + coefficients.skills_match + coefficients.experience_match + coefficients.education_match;

      // If total is not 1, adjust the experience_match coefficient
      let adjustedCoefficients = { ...coefficients };
      if (Math.abs(coefficientsTotal - 1.0) > 0.01) {
        const difference = 1.0 - coefficientsTotal;
        adjustedCoefficients.experience_match += difference;
        // Ensure experience_match doesn't go below 0
        if (adjustedCoefficients.experience_match < 0) {
          adjustedCoefficients.experience_match = 0;
        }
      }

      const coefficientState = {
        "profile_match": round2(adjustedCoefficients.profile_match),
        "skills_match": round2(adjustedCoefficients.skills_match),
        "experience_match": round2(adjustedCoefficients.experience_match * 0.6),
        "experience_years_match": round2(adjustedCoefficients.experience_match * 0.4),
        "education_match": round2(adjustedCoefficients.education_match)
      };

      // Only update state if the values have actually changed
      const previousCoefficients = previousCoefficientsRef.current;
      const currentCoefficientsStr = JSON.stringify(coefficientState);
      const previousCoefficientsStr = previousCoefficients ? JSON.stringify(previousCoefficients) : null;

      if (currentCoefficientsStr !== previousCoefficientsStr) {
        setNormaliseCoefficients(coefficientState);
        previousCoefficientsRef.current = coefficientState;
      }

      // Call the callback with the normalized coefficients if provided
      if (onNormalizedCoefficientsChange) {
        onNormalizedCoefficientsChange(coefficientState);
      }
    }
  }, [coefficients, onNormalizedCoefficientsChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setCoefficientsDropdownOpen(false);
      }
    };

    if (coefficientsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [coefficientsDropdownOpen]);

  useEffect(() => {
    // Only run this initialization once
    if (initializedRef.current) {
      return;
    }

    initializedRef.current = true;

    // if(!fieldOfStudy){
    //   console.log("fieldOfStudy", job);
    //   setFieldOfStudy(job.fieldOfStudy);
    // }

    // Initialize diploma from job data
    if (job.highest_diploma && !diploma) {
      setDiploma(job.highest_diploma);
    }

    // Initialize field of study from job data
    if (job.field_of_study && !fieldOfStudy) {
      setFieldOfStudy(job.field_of_study);
    }

    // Check if job.field_of_study exists but is not in the dropdown list
    if (job.field_of_study) {
      const predefinedFields = [
        "Computer Science", "Software Engineering", "Information Technology",
        "Data Science", "Mechanical Engineering", "Electrical Engineering",
        "Civil Engineering", "Business Administration", "Finance", "Marketing",
        "Accounting", "Economics", "Mathematics", "Physics", "Chemistry", "Biology",
        "medicine", "nursing", "pharmacy", "psychology", "sociology",
        "political_science", "history", "languages", "graphic_design",
        "architecture", "fine_arts"
      ];

      if (!predefinedFields.includes(job.field_of_study)) {
        setShowOtherFieldInput(true);
        setFieldOfStudy("other");
        setOtherFieldOfStudy(job.field_of_study);
      }
    }

    // Check if job.highest_diploma exists but is not in the dropdown list
    if (job.highest_diploma) {
      const predefinedDiplomas = [
        "High School Diploma", "Associate's Degree", "Bachelor's Degree",
        "Master's Degree", "Ph.D.", "MBA", "Professional Certification",
        "Vocational Diploma", "Technical Diploma"
      ];

      if (!predefinedDiplomas.includes(job.highest_diploma)) {
        setShowOtherDiplomaInput(true);
        setDiploma("other");
        setOtherDiploma(job.highest_diploma);
      }
    }
  }, [job]);
  useEffect(() => {
    console.log("EditWorkingDetailsDialog Props:",

      job
    );
  }, []);

  const handleDiplomaChange = (event) => {
    const value = event.target.value;
    setDiploma(value);
    setShowOtherDiplomaInput(value === "other");
    if (value !== "other") {
      setOtherDiploma("");
    }
  };

  const handleFieldOfStudyChange = (event) => {
    const value = event.target.value;
    setFieldOfStudy(value);
    setShowOtherFieldInput(value === "other");
    if (value !== "other") {
      setOtherFieldOfStudy("");
    }
  };

  const validateSecondDialog = () => {
    let tempErrors = {};
    tempErrors.location = location ? "" : "Location is required";
    tempErrors.workingMode = workingMode ? "" : "Working mode is required";
    tempErrors.postImage = job?.image ? "" : "Image is required";

    setErrors(tempErrors);
    return Object.values(tempErrors).every(x => x === "");
  };

  const processFormSubmission = async () => {
    if (validateSecondDialog()) {
      // Create second dialog data
      const secondDialogData = {
        image: job?.image,
        mode: workingMode || job.mode,
        localisation: location || job.localisation,
        age_experience: numPeople || job.age_experience,
        salary: salary !== "" ? salary : job.salary,
        currency: currency || job.currency,
        highest_diploma: diploma === "other" ? otherDiploma : diploma,
        field_of_study: fieldOfStudy === "other" ? otherFieldOfStudy : fieldOfStudy,
        experiences: experience || job.experiences
      };

      console.log("Submitting with salary:", secondDialogData.salary);
      setOpen2(false);

      try {
        await handleSave(secondDialogData);
        // Call the success callback to refresh the posts list
        onUpdateSuccess();
      } catch (error) {
        console.error("Error updating post:", error);
      }
    }
  };

  return (
    <StyledDialog
      open={open2}
      onClose={() => setOpen2(false)}
      fullWidth
      maxWidth="sm"
    >
      <Box
        sx={{
          width: "100%",
          height: 60,
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: ' 1px solid #D9D9D9',
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 20,
            fontWeight: 500,
            fontFamily: "Poppins",
            color: "#000",
          }}
        >
          {t('workingDetailsDialog.updateWorkDetails', 'Work details')}
        </DialogTitle>
        <IconButton
          onClick={() => setOpen2(false)}
          sx={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            mr: 3,
            color: "#000",
            border: '1px solid #D9D9D9',
            "&:hover": {
              color: '#000',
              backgroundColor: '#0084ff08',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <StyledDialogContent>
        <Typography
          sx={{
            color: "#004F98",
            fontSize: 15,
            fontFamily: "Poppins",
            fontWeight: "600",
          }}
        >
          {t('workingDetailsDialog.workDetails', 'Work details')}
        </Typography>
        <Stack spacing={3}>
          <ImageUploadButton
            onImageUpload={handleImageUpload}
            error={!!errors.postImage}
            helperText={errors.postImage}
            defaultValue={job?.image}
            required
          />

          {/* Field of Study and Highest Diploma on the same line */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FieldTypography>{t('workingDetailsDialog.highestDiploma', 'Highest Diploma')}*</FieldTypography>
              <StyledFormControl sx={{ width: "90%" }}>
                <Select
                  labelId="diplom-label"
                  id="diplom"
                  name="diplom"
                  value={diploma}
                  onChange={handleDiplomaChange}
                >
                  <MenuItem value="High School Diploma">{t('workingDetailsDialog.diploma.highSchool', "High School Diploma")}</MenuItem>
                  <MenuItem value="Associate's Degree">{t('workingDetailsDialog.diploma.associate', "Associate's Degree")}</MenuItem>
                  <MenuItem value="Bachelor's Degree">{t('workingDetailsDialog.diploma.bachelor', "Bachelor's Degree")}</MenuItem>
                  <MenuItem value="Master's Degree">{t('workingDetailsDialog.diploma.master', "Master's Degree")}</MenuItem>
                  <MenuItem value="Ph.D.">{t('workingDetailsDialog.diploma.phd', "Ph.D.")}</MenuItem>
                  <MenuItem value="MBA">{t('workingDetailsDialog.diploma.mba', "MBA")}</MenuItem>
                  <MenuItem value="Professional Certification">{t('workingDetailsDialog.diploma.professionalCertification', "Professional Certification")}</MenuItem>
                  <MenuItem value="Vocational Diploma">{t('workingDetailsDialog.diploma.vocational', "Vocational Diploma")}</MenuItem>
                  <MenuItem value="Technical Diploma">{t('workingDetailsDialog.diploma.technical', "Technical Diploma")}</MenuItem>
                  <MenuItem value="other">{t('workingDetailsDialog.diploma.other', "Other")}</MenuItem>
                </Select>
              </StyledFormControl>

              {showOtherDiplomaInput && (
                <StyledTextField

                  margin="normal"
                  placeholder={t('workingDetailsDialog.pleaseSpecifyDiploma', 'Please specify your diploma')}
                  value={otherDiploma}
                  onChange={(e) => setOtherDiploma(e.target.value)}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <FieldTypography>{t('workingDetailsDialog.fieldOfStudy', 'Field of Study')}*</FieldTypography>
              <StyledFormControl sx={{ width: "90%", }}>
                <Select
                  labelId="field-of-study-label"
                  id="fieldOfStudy"
                  name="fieldOfStudy"
                  value={fieldOfStudy}
                  onChange={handleFieldOfStudyChange}
                >
                  {/* Technology & Engineering */}
                  <MenuItem value="Computer Science">{t('workingDetailsDialog.field.computerScience', 'Computer Science')}</MenuItem>
                  <MenuItem value="Software Engineering">{t('workingDetailsDialog.field.softwareEngineering', 'Software Engineering')}</MenuItem>
                  <MenuItem value="Information Technology">{t('workingDetailsDialog.field.informationTechnology', 'Information Technology')}</MenuItem>
                  <MenuItem value="Data Science">{t('workingDetailsDialog.field.dataScience', 'Data Science')}</MenuItem>
                  <MenuItem value="Mechanical Engineering">{t('workingDetailsDialog.field.mechanicalEngineering', 'Mechanical Engineering')}</MenuItem>
                  <MenuItem value="Electrical Engineering">{t('workingDetailsDialog.field.electricalEngineering', 'Electrical Engineering')}</MenuItem>
                  <MenuItem value="Civil Engineering">{t('workingDetailsDialog.field.civilEngineering', 'Civil Engineering')}</MenuItem>
                  <MenuItem value="Business Administration">{t('workingDetailsDialog.field.businessAdministration', 'Business Administration')}</MenuItem>
                  <MenuItem value="Finance">{t('workingDetailsDialog.field.finance', 'Finance')}</MenuItem>
                  <MenuItem value="Marketing">{t('workingDetailsDialog.field.marketing', 'Marketing')}</MenuItem>
                  <MenuItem value="Accounting">{t('workingDetailsDialog.field.accounting', 'Accounting')}</MenuItem>
                  <MenuItem value="Economics">{t('workingDetailsDialog.field.economics', 'Economics')}</MenuItem>
                  <MenuItem value="Mathematics">{t('workingDetailsDialog.field.mathematics', 'Mathematics')}</MenuItem>
                  <MenuItem value="Physics">{t('workingDetailsDialog.field.physics', 'Physics')}</MenuItem>
                  <MenuItem value="Chemistry">{t('workingDetailsDialog.field.chemistry', 'Chemistry')}</MenuItem>
                  <MenuItem value="Biology">{t('workingDetailsDialog.field.biology', 'Biology')}</MenuItem>
                  <MenuItem value="medicine">{t('workingDetailsDialog.field.medicine', 'Medicine')}</MenuItem>
                  <MenuItem value="nursing">{t('workingDetailsDialog.field.nursing', 'Nursing')}</MenuItem>
                  <MenuItem value="pharmacy">{t('workingDetailsDialog.field.pharmacy', 'Pharmacy')}</MenuItem>
                  <MenuItem value="psychology">{t('workingDetailsDialog.field.psychology', 'Psychology')}</MenuItem>
                  <MenuItem value="sociology">{t('workingDetailsDialog.field.sociology', 'Sociology')}</MenuItem>
                  <MenuItem value="political_science">{t('workingDetailsDialog.field.politicalScience', 'Political Science')}</MenuItem>
                  <MenuItem value="history">{t('workingDetailsDialog.field.history', 'History')}</MenuItem>
                  <MenuItem value="languages">{t('workingDetailsDialog.field.languages', 'Languages')}</MenuItem>
                  <MenuItem value="graphic_design">{t('workingDetailsDialog.field.graphicDesign', 'Graphic Design')}</MenuItem>
                  <MenuItem value="architecture">{t('workingDetailsDialog.field.architecture', 'Architecture')}</MenuItem>
                  <MenuItem value="fine_arts">{t('workingDetailsDialog.field.fineArts', 'Fine Arts')}</MenuItem>
                  <MenuItem value="other">{t('workingDetailsDialog.field.other', 'Other')}</MenuItem>
                </Select>
              </StyledFormControl>

              {showOtherFieldInput && (
                <StyledTextField
                  fullWidth
                  margin="normal"
                  placeholder={t('workingDetailsDialog.pleaseSpecifyField', 'Please specify your field of study')}
                  value={otherFieldOfStudy}
                  onChange={(e) => setOtherFieldOfStudy(e.target.value)}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <FieldTypography>Expérience*</FieldTypography>
              <StyledFormControl sx={{ width: "90%" }}>
                <Select
                  labelId="number-of-people-label"
                  value={numPeople}
                  onChange={(event) => setNumPeople(event.target.value)}
                >
                  {[...Array(9).keys()].map((num) => (
                    <MenuItem key={num} value={num + 1}>
                      {num + 1}
                    </MenuItem>
                  ))}
                  <MenuItem value={9}>{t('workingDetailsDialog.ninePlus', '9+')}</MenuItem>
                </Select>
              </StyledFormControl>
            </Grid>
          </Grid>

          {/* Location, Number of Years, and Mode on another line */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FieldTypography>{t('workingDetailsDialog.location', 'Location')}*</FieldTypography>
              <GlobalCityAutocomplete
                sx={{ borderColor: "#e0e0e0" }}
                value={location ? { label: location, value: location } : null}
                onChange={(newValue) => {
                  setLocation(newValue);
                  setErrors((prev) => ({ ...prev, location: "" }));
                }}
                error={!!errors.location}
                helperText={errors.location}
              />
            </Grid>



            <Grid item xs={12} md={4}>
              <FieldTypography>{t('workingDetailsDialog.workingMode', 'Working mode')}*</FieldTypography>
              <StyledFormControl sx={{ width: "90%" }}>
                <Select
                  labelId="working-mode-label"
                  value={workingMode}
                  onChange={(event) => {
                    setWorkingMode(event.target.value);
                    setErrors((prev) => ({ ...prev, workingMode: "" }));
                  }}
                >
                  <MenuItem value="Remote">{t('workingDetailsDialog.mode.remote', 'Remote')}</MenuItem>
                  <MenuItem value="On site">{t('workingDetailsDialog.mode.onsite', 'On site')}</MenuItem>
                  <MenuItem value="Hybrid">{t('workingDetailsDialog.mode.hybrid', 'Hybrid')}</MenuItem>
                </Select>
              </StyledFormControl>
              {errors.workingMode && (
                <FormHelperText error>{errors.workingMode}</FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} md={4} >
              <SalaryInputWithCurrency
                salary={salary}
                setSalary={handleSalaryChange}
                currency={currency}
                setCurrency={setCurrency}
                  sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "50px",
                    mt: 0.2
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Experience Select */}
          {/* <Grid item xs={12} sm={12}>
              <FieldTypography>Experience Required*</FieldTypography>
              <TextEditor
                experience={experience || ""}
                setExperience={setExperience} 
              />
            </Grid> */}



          {/* Coefficients Dropdown - Disabled */}
          <Grid item xs={12}>
            <Box sx={{ position: 'relative' }} ref={dropdownRef}>
              <Typography
                component="span"
                color={"primary"}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "default"
                }}
              >
                Les paramètres de matching
                {coefficients && (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      px: 1,
                      py: 0.25,
                      bgcolor: 'success.main',
                      color: 'white',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    configuré
                  </Box>
                )}
              </Typography>

              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  mt: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '10px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  boxShadow: 'none',
                  pointerEvents: 'none',
                  opacity: 0.7
                }}
              >
                <Box p={0}>
                  <MatchingCoefficients
                    initialWeights={coefficients}
                    onWeightsChange={() => {}}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>

        </Stack>
      </StyledDialogContent>
      <DialogActions
        sx={{
          pr: 3,
          pb: 2,
        }}
      >
        <NewCancelButton
          onClick={() => {
            setOpen(true);
            setOpen2(false);
          }}
        >
          {t('workingDetailsDialog.previous', 'Previous')}
        </NewCancelButton>
        <NewCreateOfferButton onClick={processFormSubmission}>{t("workingDetailsDialog.Edit_offer")}</NewCreateOfferButton>
      </DialogActions>
    </StyledDialog>
  );
}

export default EditWorkingDetailsDialog;
